import React, { useEffect, useState, useCallback } from 'react';
import MostMeetPRsTable from './MostMeetPRsTable';
import PropTypes from 'prop-types';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { toast } from 'react-toastify';
import { getMostMeetPRsReport } from 'api/reportsApi';

const MostMeetPRs = ({ clubID, onDataChange }) => {
  const storageKey = `mostMeetPRsFilters_${clubID}`;

  // Load saved filters from SessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedFilters = getSavedFilters();

  const [athleteData, setAthleteData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [minPRsBeaten, setMinPRsBeaten] = useState(savedFilters?.minPRsBeaten ?? 2);
  const [loading, setLoading] = useState(true);
  const [seasonName, setSeasonName] = useState('');

  // Save filters to SessionStorage whenever they change
  useEffect(() => {
    const filters = {
      minPRsBeaten,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [minPRsBeaten, storageKey]);

  const fetchMostMeetPRs = useCallback(async () => {
    try {
      if (clubID) {
        setLoading(true);
        const response = await getMostMeetPRsReport(clubID, minPRsBeaten);
        const data = response.data || {};
        const athletes = data.athletes || [];
        setAthleteData(athletes);
        setSeasonName(data.season?.seasonName || '');
      }
      setLoading(false);
    } catch (err) {
      console.error('[MostMeetPRs] Fetch most meet PRs error: ', err);
      toast.error('Failed to load best meet performance report');
      setLoading(false);
    }
  }, [clubID, minPRsBeaten]);

  useEffect(() => {
    if (clubID) {
      fetchMostMeetPRs();
    }
  }, [fetchMostMeetPRs, clubID]);

  useEffect(() => {
    if (athleteData?.length) {
      let results = athleteData;

      // Apply search filter (client-side only)
      if (searchText) {
        results = results.filter((p) => {
          const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
          return fullName.includes(searchText.toLowerCase());
        });
      }

      setFilteredData(results);
    } else {
      setFilteredData([]);
    }
  }, [searchText, athleteData]);

  // Notify parent component of data changes (for export button)
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        hasData: filteredData.length > 0,
        minPRsBeaten,
        seasonName,
      });
    }
  }, [filteredData, minPRsBeaten, seasonName, onDataChange]);

  return (
    <>
      <LoadingContent loading={loading}>
        <MostMeetPRsTable
          athleteData={filteredData}
          setSearchText={setSearchText}
          minPRsBeaten={minPRsBeaten}
          setMinPRsBeaten={setMinPRsBeaten}
        />
      </LoadingContent>
    </>
  );
};

MostMeetPRs.propTypes = {
  clubID: PropTypes.string,
  onDataChange: PropTypes.func,
};

export default MostMeetPRs;
